import type { PropsWithChildren, ReactNode } from 'react'
import {
  AuditOutlined,
  ClusterOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import { Card, Flex, Grid, Space, Typography, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import { isRtl } from '@/app/i18n'
import { useThemeMode } from '@/app/theme/useThemeMode'
import { LanguageSwitcher } from '@/shared/ui/LanguageSwitcher'
import { NetlinkLogo } from '@/shared/ui/NetlinkLogo'
import { NetworkBackdrop } from '@/shared/ui/NetworkBackdrop'
import { ThemeToggle } from '@/shared/ui/ThemeToggle'

interface AuthShellProps extends PropsWithChildren {
  title: string
  subtitle?: ReactNode
  /** Tenant tanlash kartalari kabi kengroq tarkib uchun — standart 400px */
  width?: number
}

const GUARANTEES = [
  { icon: <SafetyCertificateOutlined />, key: 'totp' },
  { icon: <AuditOutlined />, key: 'audit' },
  { icon: <ClusterOutlined />, key: 'tenant' },
] as const

/**
 * Login, tenant tanlash va TOTP qobig'i. Keng ekranda chapda brend va
 * tizim kafolatlari, o'ngda forma; tor ekranda faqat logotip va forma.
 */
export function AuthShell({ title, subtitle, width = 400, children }: AuthShellProps) {
  const { token } = theme.useToken()
  const { t } = useTranslation()
  const { isDark } = useThemeMode()
  const screens = Grid.useBreakpoint()
  const wide = screens.lg === true

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: token.colorBgLayout }}>
      <Flex gap="small" style={{ position: 'absolute', top: 16, insetInlineEnd: 16, zIndex: 2 }}>
        <LanguageSwitcher />
        <ThemeToggle />
      </Flex>

      {wide ? (
        <div
          style={{
            position: 'relative',
            flex: '1 1 0',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            background: [
              `radial-gradient(100% 80% at ${isRtl() ? '100%' : '0%'} 0%, ${token.colorPrimaryBg} 0%, transparent 60%)`,
              `linear-gradient(160deg, ${token.colorBgContainer} 0%, ${token.colorBgLayout} 100%)`,
            ].join(', '),
            borderInlineEnd: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <NetworkBackdrop opacity={isDark ? 0.45 : 0.35} />

          <Flex vertical gap={32} style={{ position: 'relative', padding: '48px 64px', maxWidth: 620 }}>
            <Typography.Title level={1} style={{ margin: 0, lineHeight: 0 }}>
              <NetlinkLogo variant="horizontal" height={88} onDark={isDark} />
            </Typography.Title>

            <Typography.Paragraph
              type="secondary"
              style={{ fontSize: 16, lineHeight: 1.6, margin: 0, maxWidth: 460 }}
            >
              {t('auth.brandTagline')}
            </Typography.Paragraph>

            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {GUARANTEES.map((g) => (
                <Card key={g.key} size="small" style={{ background: token.colorBgElevated }}>
                  <Flex gap={12} align="flex-start">
                    <span style={{ color: token.colorPrimary, fontSize: 18, lineHeight: '22px' }}>
                      {g.icon}
                    </span>
                    <div>
                      <Typography.Text strong>{t(`auth.guarantee.${g.key}.title`)}</Typography.Text>
                      <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
                        {t(`auth.guarantee.${g.key}.text`)}
                      </Typography.Paragraph>
                    </div>
                  </Flex>
                </Card>
              ))}
            </Space>
          </Flex>
        </div>
      ) : null}

      <Flex
        align="center"
        justify="center"
        style={{ flex: wide ? '0 0 clamp(420px, 34vw, 560px)' : '1 1 auto', padding: 24 }}
      >
        <div style={{ width: '100%', maxWidth: width }}>
          {!wide ? (
            <Typography.Title level={1} style={{ margin: '0 0 24px', lineHeight: 0 }}>
              <NetlinkLogo variant="stacked" height={112} onDark={isDark} style={{ marginInline: 'auto' }} />
            </Typography.Title>
          ) : null}

          <Card>
            <Flex vertical gap="large">
              <div>
                <Typography.Title level={3} style={{ marginBottom: 0 }}>
                  {title}
                </Typography.Title>
                {subtitle ? <Typography.Text type="secondary">{subtitle}</Typography.Text> : null}
              </div>

              {children}
            </Flex>
          </Card>
        </div>
      </Flex>
    </div>
  )
}
